// (c) TwistedMinds, Gerry Uytdenhouwen - 4 november 2026

/**
 * Display helpers for the "My orders" list in the order widget.
 */

const OPEN_STATUSES = ['created', 'open', 'pending', 'authorized'];
const FINAL_STATUSES = ['paid', 'canceled', 'expired', 'failed'];

/**
 * Maps a stored order status to one of the five statuses the widget has a label for
 * @param {string} status
 * @return {'open'|'paid'|'canceled'|'expired'|'failed'}
 */
export function orderStatusKey(status) {
    const value = String(status ?? '').trim().toLowerCase();
    if (OPEN_STATUSES.includes(value)) {
        return 'open';
    }
    if (FINAL_STATUSES.includes(value)) {
        return value;
    }
    return 'failed';
}

/**
 * Maps a stored delivery type to the kind the widget shows an icon/label for
 * @param {string} type
 * @return {'pickup'|'delivery'|'vending'}
 */
export function deliveryKind(type) {
    if (type === 'delivery') {
        return 'delivery';
    }
    if (type === 'pick_up_vending') {
        return 'vending';
    }
    return 'pickup';
}

/**
 * Short, localised moment of an order: "Za 31 okt · 10:30" (no time: "Za 31 okt")
 * @param {string} date  YYYY-MM-DD
 * @param {string|null} time  e.g. "10:30"
 * @param {string} locale
 * @return {string} empty when the date is not valid
 */
export function formatOrderMoment(date, time, locale) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date ?? ''));
    if (!match) {
        return '';
    }
    const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (Number.isNaN(day.getTime())) {
        return '';
    }
    const text = new Intl.DateTimeFormat(locale, {weekday: 'short', day: 'numeric', month: 'short'}).format(day);
    const label = text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
    return time ? label + ' · ' + time : label;
}

/**
 * When an order was placed, split for "besteld op {date} om {time}": date "28 okt", time "13:02"
 * @param {string} createdAt  "YYYY-MM-DD HH:MM:SS"
 * @param {string} locale
 * @return {{date: string, time: string}|null} null when the value is not readable
 */
export function formatOrderedOn(createdAt, locale) {
    const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(createdAt ?? ''));
    if (!match) {
        return null;
    }
    const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (Number.isNaN(day.getTime())) {
        return null;
    }
    const date = new Intl.DateTimeFormat(locale, {day: 'numeric', month: 'short'}).format(day);
    return {date, time: match[4] + ':' + match[5]};
}

/**
 * Amount of an order as money ("€ 42,80"); empty when there is no amount (order without a cart)
 * @param {number|string|null} amount
 * @param {string} locale
 * @return {string}
 */
export function formatOrderAmount(amount, locale) {
    if (amount === null || amount === undefined || String(amount).trim() === '') {
        return '';
    }
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
        return '';
    }
    return new Intl.NumberFormat(locale, {style: 'currency', currency: 'EUR'}).format(value);
}

/**
 * One product line of an order: "2 x Kotelet (500 g, gesneden)"
 * @param {{name: string, qty: number|string, variation: string|null}} line
 * @return {string}
 */
export function orderLineText(line) {
    const options = String(line?.variation ?? '')
        .split(',')
        .map((option) => option.trim())
        .filter((option) => option !== '');
    const text = `${line?.qty ?? ''} x ${line?.name ?? ''}`;
    return options.length > 0 ? `${text} (${options.join(', ')})` : text;
}

/**
 * Initials for the avatar: "An Vermeulen" -> "AV", "Jan" -> "J"; first and last word only
 * @param {string|null} name
 * @return {string}
 */
export function customerInitials(name) {
    const words = String(name ?? '').trim().split(/\s+/).filter((word) => word !== '');
    if (words.length === 0) {
        return '';
    }
    const first = words[0].charAt(0);
    const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
}
